/**
 * Represents a custom Google Map for the SunRail website.
 * @class CustomGoogleMap
 */

/* global google */
class CustomGoogleMap {
	/**
	 * @class
	 * @classdesc Represents a constructor for creating a map object.
	 *
	 * @param {string} mapElementId - The id of the HTML element where the map will be displayed.
	 * @param {Object} mapOptions   - The options to configure the map object.
	 */
	constructor( mapElementId, mapOptions ) {
		this.mapElement = this.getElement( mapElementId );
		this.mapOptions = mapOptions;
		this.map = null;
		this.defaultCenterPoint = mapOptions.center;
		this.selectedTrain = null;

		this.cacheDomElements();
	}

	/**
	 * Caches DOM elements for easy access and manipulation.
	 *
	 * @function cacheDomElements
	 *
	 * @return {void}
	 */
	cacheDomElements() {
		this.siteHeader = this.queryElement( '.site-header' );
		this.mainDiv = this.queryElement( '.sunrail_map_wrapper' );
		this.menu = this.mainDiv.querySelector( '.menu' );
		this.tabs = this.queryAllElements( '.tab' );
		this.contents = this.queryAllElements( '.map-contents' );
		this.trainsContent = this.getElement( 'trains' );
		this.lists = this.queryAllElements( '.list' );
		this.listItems = this.queryAllElements( '.list .station, .list .train' );
		this.trainListParent = this.queryElement( '#trains .list .list-wrapper' );
		this.detailDivs = this.queryAllElements( '.details' );
		this.stationClose = this.getElement( 'stations-close' );
		this.trainClose = this.getElement( 'trains-close' );
		this.footerIcon = this.queryElement( '.footer-button' );
		this.siteFooter = this.queryElement( '.site-footer' );
		this.userButtons = this.queryElement( '.user-buttons' );
		this.trainsClosed = this.getElement( 'train-not-scheduled' );
		this.trainsOpened = this.getElement( 'trains-available' );
		this.trainMenuHeaderText = this.getElement( 'train-menu-header-text' );
		this.dataSetId = window.mapSettings.dataSetId || '7ae056cb-1ae2-43ca-bcff-660bcea7259b';

		this.latlng = null;
		this.polyline = null;
		this.markersStation = [];
		this.markersTrain = [];
		this.markersTrainByID = [];
		this.currentCenterPoint = null;
		this.railLineInitialized = false;
	}

	/**
	 * Get the element by its ID
	 *
	 * @param {string} id - The ID of the element to retrieve
	 *
	 * @return {Element} - The element with the specified ID, or null
	 */
	getElement( id ) {
		return document.getElementById( id );
	}

	/**
	 * Retrieves the first element in the document that matches the specified CSS selector.
	 *
	 * @param {string} selector - The CSS selector to match against elements in the document.
	 * @return {Element|null} - The first matching
	 */
	queryElement( selector ) {
		return document.querySelector( selector );
	}

	/**
	 * Retrieves all elements in the document that match the specified CSS selector.
	 *
	 * @param {string} selector - The CSS selector used to filter the elements.
	 * @return {NodeList} - A collection of elements that match the
	 */
	queryAllElements( selector ) {
		return document.querySelectorAll( selector );
	}

	/**
	 * Initializes the map and sets up necessary components.
	 *
	 * @return {void}
	 */
	async initialize() {
		if ( ! this.mapElement ) {
			console.error( 'Map element not found' ); // eslint-disable-line no-console
			return;
		}

		this.adjustContainerHeight();

		const isWebGLSupported = this.isWebGLSupported();
		const { Map, MapTypeId } = await google.maps.importLibrary( 'maps' );
		let Polyline, LatLng, datasetLayer;

		this.mapOptions.center = this.defaultCenterPoint;
		this.mapOptions.mapTypeId = MapTypeId.ROADMAP;
		this.map = new Map( this.mapElement, this.mapOptions );
		this.mapCapabilities = this.map.getMapCapabilities();

		// Load Polyline and LatLng only when needed
		if ( ! isWebGLSupported || ! this.mapCapabilities.isDataDrivenStylingAvailable ) {
			( { Polyline } = await google.maps.importLibrary( 'maps' ) );
			( { LatLng } = await google.maps.importLibrary( 'core' ) );
			this.polyline = Polyline;
			this.latlng = LatLng;
			this.createRailLine();
			this.railLineInitialized = true;
		} else {
			datasetLayer = this.map.getDatasetFeatureLayer( this.dataSetId );
			this.setLayerStyle( datasetLayer );
		}

		// Add event listeners
		this.map.addListener( 'mapcapabilities_changed', this.mapCapabilitiesChanged.bind( this ) );
		this.map.addListener( 'renderingtype_changed', this.mapCapabilitiesChanged.bind( this ) );

		await this.loadStations();
		await this.loadTrains();
		this.addContent();

		window.addEventListener( 'resize', this.resizeMap.bind( this ) );
	}

	/**
	 * Updates the map capabilities and initializes the rail line if necessary.
	 *
	 * @return {void}
	 */
	async mapCapabilitiesChanged() {
		// Update map capabilities
		this.mapCapabilities = this.map.getMapCapabilities();

		// Check if the rail line has already been initialized
		if ( this.railLineInitialized ) {
			return; // Skip if already initialized
		}

		// Check conditions
		if ( ! this.mapCapabilities.isDataDrivenStylingAvailable || this.map.getRenderingType() === 'RASTER' ) {
			// Load necessary libraries and create rail line
			const { Polyline } = await google.maps.importLibrary( 'maps' );
			const { LatLng } = await google.maps.importLibrary( 'core' );
			this.polyline = Polyline;
			this.latlng = LatLng;
			this.createRailLine();
			this.railLineInitialized = true;
		}
	}

	/**
	 * Adjusts the height of the container based on the device type.
	 * If the device is not mobile, it calculates the new height based on the window height and the height of the site header.
	 * If the device is mobile, it sets the height to 100vh.
	 *
	 * @return {void}
	 */
	adjustContainerHeight() {
		if ( ! this.isMobile() ) {
			document.documentElement.style.overflowY = 'hidden';
			this.mapElement.style.height = window.innerHeight - this.siteHeader.getBoundingClientRect().height + 'px';
			this.lists.forEach(
				( list ) =>
					( list.style.maxHeight =
						window.innerHeight - this.siteHeader.getBoundingClientRect().height - 100 + 'px' )
			);
			const target = this.queryElement( '#stations' );
			this.activateTab( 'stations' );
			this.activateContent( target );
		} else {
			this.mapElement.style.height = `100vh`;
		}
	}

	/**
	 * Checks if WebGL is supported in the current browser.
	 *
	 * @return {boolean} - True if WebGL is supported, false otherwise.
	 */
	isWebGLSupported() {
		try {
			const canvas = document.createElement( 'canvas' );
			return !! window.WebGL2RenderingContext && !! canvas.getContext( 'webgl2' );
		} catch ( e ) {
			return false;
		}
	}

	/**
	 * Creates a rail line on the map using the rail line coordinates.
	 *
	 * @return {void}
	 */
	createRailLine() {
		if ( ! this.polyline || ! this.latlng ) {
			console.error( 'Polyline or LatLng not defined' ); // eslint-disable-line no-console
			return;
		}

		const railLineCoordinates = this.getRailLineCoordinates();
		const path = railLineCoordinates.map( ( coord ) => new this.latlng( coord.lat(), coord.lng() ) );

		const railLine = new this.polyline( {
			path,
			strokeColor: '#004964',
			strokeOpacity: 1.0,
			strokeWeight: 5,
		} );

		railLine.setMap( this.map );
	}

	/**
	 * Sets the style for a layer.
	 *
	 * @param {Object} layer               - The layer object to set the style for.
	 * @param {string} layer.strokeColor   - The stroke color to apply to the layer.
	 * @param {number} layer.strokeWeight  - The stroke weight to apply to the layer.
	 * @param {number} layer.strokeOpacity - The stroke opacity to apply to the layer.
	 * @param {string} layer.fillColor     - The fill color to apply to the layer.
	 * @param {number} layer.fillOpacity   - The fill opacity to apply to the layer.
	 */
	setLayerStyle( layer ) {
		if ( ! layer ) {
			console.error( 'Layer not defined' ); // eslint-disable-line no-console
			return;
		}

		layer.style = {
			strokeColor: '#004964',
			strokeWeight: 5,
			strokeOpacity: 1,
			fillColor: '#004964',
			fillOpacity: 1,
		};
	}

	/**
	 * Retrieves the coordinates of a rail line.
	 *
	 * @return {Array} An array containing the coordinates of the rail line.
	 */
	getRailLineCoordinates() {
		return [
			new this.latlng( 29.01777592, -81.35272464 ),
			new this.latlng( 29.0103253, -81.35015783 ),
			new this.latlng( 29.0048488, -81.34809019 ),
			new this.latlng( 29.00327017, -81.34749158 ),
			new this.latlng( 29.00269137, -81.34723651 ),
			new this.latlng( 29.002284, -81.3469803 ),
			new this.latlng( 29.0020608, -81.34682131 ),
			new this.latlng( 29.00173174, -81.34653782 ),
			new this.latlng( 29.00149562, -81.34631982 ),
			new this.latlng( 29.00096999, -81.34581037 ),
			new this.latlng( 29.00059259, -81.34544655 ),
			new this.latlng( 29.00021015, -81.34509566 ),
			new this.latlng( 28.99959404, -81.34450002 ),
			new this.latlng( 28.99937969, -81.34429137 ),
			new this.latlng( 28.99921381, -81.34413962 ),
			new this.latlng( 28.99901131, -81.34396007 ),
			new this.latlng( 28.99890359, -81.34387029 ),
			new this.latlng( 28.99874835, -81.34375069 ),
			new this.latlng( 28.9984186, -81.34352958 ),
			new this.latlng( 28.99818905, -81.34338303 ),
			new this.latlng( 28.99789234, -81.34320264 ),
			new this.latlng( 28.99758978, -81.34302242 ),
			new this.latlng( 28.99659846, -81.34242842 ),
			new this.latlng( 28.99557829, -81.34181779 ),
			new this.latlng( 28.99226828, -81.33984286 ),
			new this.latlng( 28.990573, -81.33884003 ),
			new this.latlng( 28.98981043, -81.33836596 ),
			new this.latlng( 28.98886048, -81.3378019 ),
			new this.latlng( 28.98774527, -81.33713521 ),
			new this.latlng( 28.98358902, -81.33465244 ),
			new this.latlng( 28.98181487, -81.33360092 ),
			new this.latlng( 28.98156317, -81.33344532 ),
			new this.latlng( 28.98129942, -81.33328703 ),
			new this.latlng( 28.98085564, -81.33304613 ),
			new this.latlng( 28.98061778, -81.33291454 ),
			new this.latlng( 28.98048176, -81.33284257 ),
			new this.latlng( 28.98034286, -81.33277984 ),
			new this.latlng( 28.98016556, -81.33270842 ),
			new this.latlng( 28.97998699, -81.33263562 ),
			new this.latlng( 28.97970068, -81.33254073 ),
			new this.latlng( 28.97948399, -81.3324719 ),
			new this.latlng( 28.97921045, -81.33239635 ),
			new this.latlng( 28.97880478, -81.3323107 ),
			new this.latlng( 28.97858202, -81.33227236 ),
			new this.latlng( 28.97828282, -81.33223351 ),
			new this.latlng( 28.97771994, -81.33219842 ),
			new this.latlng( 28.97687799, -81.33218643 ),
			new this.latlng( 28.97537559, -81.33219137 ),
			new this.latlng( 28.97353483, -81.33219335 ),
			new this.latlng( 28.97316116, -81.33219806 ),
			new this.latlng( 28.97135833, -81.33219889 ),
			new this.latlng( 28.96996811, -81.33220231 ),
			new this.latlng( 28.96910673, -81.33220387 ),
			new this.latlng( 28.96598985, -81.33221408 ),
			new this.latlng( 28.96226672, -81.33222478 ),
			new this.latlng( 28.9584008, -81.33223922 ),
			new this.latlng( 28.95611236, -81.33224333 ),
			new this.latlng( 28.9543846, -81.3322391 ),
			new this.latlng( 28.95236025, -81.33225248 ),
			new this.latlng( 28.95040287, -81.33225958 ),
			new this.latlng( 28.94856465, -81.33226072 ),
			new this.latlng( 28.94642119, -81.33227622 ),
			new this.latlng( 28.94443833, -81.33228739 ),
			new this.latlng( 28.94297213, -81.33228319 ),
			new this.latlng( 28.94064149, -81.33229102 ),
			new this.latlng( 28.94018931, -81.33229088 ),
			new this.latlng( 28.93960654, -81.33229952 ),
			new this.latlng( 28.93888539, -81.33234506 ),
			new this.latlng( 28.93876193, -81.33235963 ),
			new this.latlng( 28.93771426, -81.33248662 ),
			new this.latlng( 28.93659691, -81.33265996 ),
			new this.latlng( 28.93553647, -81.33280863 ),
			new this.latlng( 28.93515366, -81.3328537 ),
			new this.latlng( 28.93485709, -81.33287859 ),
			new this.latlng( 28.93441009, -81.33290814 ),
			new this.latlng( 28.92854151, -81.33293198 ),
			new this.latlng( 28.92238367, -81.33294598 ),
			new this.latlng( 28.92035443, -81.33294941 ),
			new this.latlng( 28.91937426, -81.33295377 ),
			new this.latlng( 28.91878067, -81.33296093 ),
			new this.latlng( 28.91847759, -81.33296889 ),
			new this.latlng( 28.91802484, -81.33300477 ),
			new this.latlng( 28.91765431, -81.33306043 ),
			new this.latlng( 28.91713818, -81.33317249 ),
			new this.latlng( 28.91668624, -81.33330008 ),
			new this.latlng( 28.91187673, -81.33476754 ),
			new this.latlng( 28.90662476, -81.33637804 ),
			new this.latlng( 28.90029357, -81.33832085 ),
			new this.latlng( 28.89876215, -81.33878144 ),
			new this.latlng( 28.89827201, -81.33893175 ),
			new this.latlng( 28.89773974, -81.33907245 ),
			new this.latlng( 28.89715344, -81.33921262 ),
			new this.latlng( 28.89670491, -81.33930175 ),
			new this.latlng( 28.895959, -81.33941143 ),
			new this.latlng( 28.89550686, -81.33944652 ),
			new this.latlng( 28.89528427, -81.33947116 ),
			new this.latlng( 28.89465565, -81.33949388 ),
			new this.latlng( 28.89413844, -81.33950106 ),
			new this.latlng( 28.89322611, -81.33945387 ),
			new this.latlng( 28.89015205, -81.33921536 ),
			new this.latlng( 28.88357807, -81.33868778 ),
			new this.latlng( 28.87729487, -81.33816494 ),
			new this.latlng( 28.87625417, -81.33807759 ),
			new this.latlng( 28.87579637, -81.33801326 ),
			new this.latlng( 28.87550059, -81.33796656 ),
			new this.latlng( 28.87490961, -81.33782486 ),
			new this.latlng( 28.87440753, -81.33765799 ),
			new this.latlng( 28.87412728, -81.33754968 ),
			new this.latlng( 28.87348989, -81.33723919 ),
			new this.latlng( 28.87328585, -81.33712363 ),
			new this.latlng( 28.87295841, -81.33691745 ),
			new this.latlng( 28.87257404, -81.33664819 ),
			new this.latlng( 28.872206, -81.33635747 ),
			new this.latlng( 28.87196824, -81.33614653 ),
			new this.latlng( 28.87162171, -81.33581947 ),
			new this.latlng( 28.87117061, -81.33536759 ),
			new this.latlng( 28.8705538, -81.33473654 ),
			new this.latlng( 28.86885704, -81.3330105 ),
			new this.latlng( 28.86607709, -81.3301921 ),
			new this.latlng( 28.86336567, -81.32743745 ),
			new this.latlng( 28.86232632, -81.32638178 ),
			new this.latlng( 28.86175606, -81.32579832 ),
			new this.latlng( 28.86126969, -81.32530847 ),
			new this.latlng( 28.86104641, -81.32508402 ),
			new this.latlng( 28.86081483, -81.324864 ),
			new this.latlng( 28.86063936, -81.32469664 ),
			new this.latlng( 28.86039728, -81.3244904 ),
			new this.latlng( 28.86015038, -81.32430105 ),
			new this.latlng( 28.85988263, -81.32412447 ),
			new this.latlng( 28.85964705, -81.32398699 ),
			new this.latlng( 28.85945716, -81.32388713 ),
			new this.latlng( 28.85924761, -81.32379021 ),
			new this.latlng( 28.85890013, -81.32365487 ),
			new this.latlng( 28.85860574, -81.32356272 ),
			new this.latlng( 28.85826222, -81.32348212 ),
			new this.latlng( 28.85804685, -81.32345123 ),
			new this.latlng( 28.85774906, -81.32341387 ),
			new this.latlng( 28.85745018, -81.32340599 ),
			new this.latlng( 28.85677377, -81.32340199 ),
			new this.latlng( 28.85634213, -81.32340341 ),
			new this.latlng( 28.855866, -81.323386 ),
			new this.latlng( 28.853817, -81.323354 ),
			new this.latlng( 28.850867, -81.323306 ),
			new this.latlng( 28.847314, -81.323247 ),
			new this.latlng( 28.845472, -81.32322 ),
			new this.latlng( 28.844815, -81.323247 ),
			new this.latlng( 28.842775, -81.323231 ),
			new this.latlng( 28.841958, -81.323327 ),
			new this.latlng( 28.84106, -81.323547 ),
			new this.latlng( 28.839392, -81.324116 ),
			new this.latlng( 28.836272, -81.325178 ),
			new this.latlng( 28.833588, -81.326069 ),
			new this.latlng( 28.832865, -81.326133 ),
			new this.latlng( 28.832277, -81.32609 ),
			new this.latlng( 28.831685, -81.32594 ),
			new this.latlng( 28.831154, -81.325709 ),
			new this.latlng( 28.830618, -81.325355 ),
			new this.latlng( 28.830106, -81.324851 ),
			new this.latlng( 28.829453, -81.323912 ),
			new this.latlng( 28.812096, -81.295996 ),
			new this.latlng( 28.810385, -81.293571 ),
			new this.latlng( 28.8089, -81.292412 ),
			new this.latlng( 28.807245, -81.291854 ),
			new this.latlng( 28.797618, -81.291769 ),
			new this.latlng( 28.78784, -81.291769 ),
			new this.latlng( 28.785545, -81.292133 ),
			new this.latlng( 28.783947, -81.292841 ),
			new this.latlng( 28.781991, -81.294215 ),
			new this.latlng( 28.778963, -81.297412 ),
			new this.latlng( 28.77365, -81.302948 ),
			new this.latlng( 28.769879, -81.306885 ),
			new this.latlng( 28.768976, -81.307669 ),
			new this.latlng( 28.76781, -81.308227 ),
			new this.latlng( 28.765684, -81.308967 ),
			new this.latlng( 28.764217, -81.309793 ),
			new this.latlng( 28.762374, -81.31122 ),
			new this.latlng( 28.7618, -81.312014 ),
			new this.latlng( 28.75927, -81.316971 ),
			new this.latlng( 28.758202, -81.319111 ),
			new this.latlng( 28.757826, -81.319728 ),
			new this.latlng( 28.757318, -81.320307 ),
			new this.latlng( 28.75649, -81.32093 ),
			new this.latlng( 28.755653, -81.321262 ),
			new this.latlng( 28.754939, -81.321359 ),
			new this.latlng( 28.754431, -81.321337 ),
			new this.latlng( 28.751435, -81.321117 ),
			new this.latlng( 28.748251, -81.320854 ),
			new this.latlng( 28.747357, -81.320854 ),
			new this.latlng( 28.746083, -81.321058 ),
			new this.latlng( 28.744841, -81.321493 ),
			new this.latlng( 28.743759, -81.322104 ),
			new this.latlng( 28.742748, -81.322925 ),
			new this.latlng( 28.739756, -81.325629 ),
			new this.latlng( 28.734799, -81.33014 ),
			new this.latlng( 28.73382, -81.330972 ),
			new this.latlng( 28.730617, -81.333354 ),
			new this.latlng( 28.728392, -81.334957 ),
			new this.latlng( 28.727315, -81.335569 ),
			new this.latlng( 28.725828, -81.336148 ),
			new this.latlng( 28.72445, -81.336454 ),
			new this.latlng( 28.723246, -81.336556 ),
			new this.latlng( 28.721326, -81.33654 ),
			new this.latlng( 28.716132, -81.336492 ),
			new this.latlng( 28.714509, -81.336722 ),
			new this.latlng( 28.712966, -81.337194 ),
			new this.latlng( 28.711348, -81.338117 ),
			new this.latlng( 28.708299, -81.340714 ),
			new this.latlng( 28.705382, -81.343246 ),
			new this.latlng( 28.699792, -81.346056 ),
			new this.latlng( 28.69614, -81.348503 ),
			new this.latlng( 28.693261, -81.34979 ),
			new this.latlng( 28.691171, -81.350198 ),
			new this.latlng( 28.688969, -81.350241 ),
			new this.latlng( 28.67871, -81.349125 ),
			new this.latlng( 28.676225, -81.349232 ),
			new this.latlng( 28.67374, -81.34994 ),
			new this.latlng( 28.671523, -81.351383 ),
			new this.latlng( 28.669151, -81.353379 ),
			new this.latlng( 28.668139, -81.354125 ),
			new this.latlng( 28.666849, -81.354897 ),
			new this.latlng( 28.663394, -81.356791 ),
			new this.latlng( 28.66185, -81.357472 ),
			new this.latlng( 28.660005, -81.35796 ),
			new this.latlng( 28.657783, -81.358379 ),
			new this.latlng( 28.655947, -81.358561 ),
			new this.latlng( 28.655039, -81.358545 ),
			new this.latlng( 28.653542, -81.3584 ),
			new this.latlng( 28.65108, -81.358024 ),
			new this.latlng( 28.646099, -81.357279 ),
			new this.latlng( 28.643025, -81.356823 ),
			new this.latlng( 28.642173, -81.356775 ),
			new this.latlng( 28.641349, -81.356876 ),
			new this.latlng( 28.640591, -81.357107 ),
			new this.latlng( 28.639819, -81.357499 ),
			new this.latlng( 28.639216, -81.357939 ),
			new this.latlng( 28.638571, -81.358566 ),
			new this.latlng( 28.636353, -81.361071 ),
			new this.latlng( 28.635746, -81.361613 ),
			new this.latlng( 28.63512, -81.362016 ),
			new this.latlng( 28.629861, -81.364762 ),
			new this.latlng( 28.628975, -81.365191 ),
			new this.latlng( 28.628283, -81.365395 ),
			new this.latlng( 28.62777, -81.365449 ),
			new this.latlng( 28.625995, -81.365433 ),
			new this.latlng( 28.625486, -81.365492 ),
			new this.latlng( 28.624973, -81.365663 ),
			new this.latlng( 28.62211, -81.36709 ),
			new this.latlng( 28.621455, -81.367337 ),
			new this.latlng( 28.620895, -81.367439 ),
			new this.latlng( 28.62057, -81.367455 ),
			new this.latlng( 28.619449, -81.367444 ),
			new this.latlng( 28.616266, -81.367364 ),
			new this.latlng( 28.615649, -81.367267 ),
			new this.latlng( 28.615004, -81.367047 ),
			new this.latlng( 28.614284, -81.366683 ),
			new this.latlng( 28.610266, -81.364027 ),
			new this.latlng( 28.609023, -81.363126 ),
			new this.latlng( 28.608482, -81.362536 ),
			new this.latlng( 28.606169, -81.359586 ),
			new this.latlng( 28.60096, -81.352896 ),
			new this.latlng( 28.600343, -81.352333 ),
			new this.latlng( 28.599538, -81.351888 ),
			new this.latlng( 28.598657, -81.351705 ),
			new this.latlng( 28.596698, -81.3517 ),
			new this.latlng( 28.596038, -81.351861 ),
			new this.latlng( 28.595473, -81.352134 ),
			new this.latlng( 28.595016, -81.352494 ),
			new this.latlng( 28.594559, -81.353009 ),
			new this.latlng( 28.592035, -81.356952 ),
			new this.latlng( 28.591606, -81.357488 ),
			new this.latlng( 28.591239, -81.357858 ),
			new this.latlng( 28.586514, -81.361978 ),
			new this.latlng( 28.585864, -81.362611 ),
			new this.latlng( 28.585436, -81.363147 ),
			new this.latlng( 28.582067, -81.368984 ),
			new this.latlng( 28.581667, -81.369574 ),
			new this.latlng( 28.581286, -81.369992 ),
			new this.latlng( 28.580819, -81.370373 ),
			new this.latlng( 28.580188, -81.370744 ),
			new this.latlng( 28.579425, -81.371044 ),
			new this.latlng( 28.578516, -81.371258 ),
			new this.latlng( 28.577771, -81.371344 ),
			new this.latlng( 28.577055, -81.371344 ),
			new this.latlng( 28.574186, -81.371264 ),
			new this.latlng( 28.573687, -81.371291 ),
			new this.latlng( 28.571444, -81.371741 ),
			new this.latlng( 28.567831, -81.372479 ),
			new this.latlng( 28.567216, -81.372573 ),
			new this.latlng( 28.566757, -81.372581 ),
			new this.latlng( 28.564076, -81.372465 ),
			new this.latlng( 28.563473, -81.372468 ),
			new this.latlng( 28.56298, -81.372538 ),
			new this.latlng( 28.562469, -81.372688 ),
			new this.latlng( 28.561953, -81.372908 ),
			new this.latlng( 28.561357, -81.373294 ),
			new this.latlng( 28.560971, -81.373627 ),
			new this.latlng( 28.56062, -81.374008 ),
			new this.latlng( 28.560328, -81.374407 ),
			new this.latlng( 28.559814, -81.375357 ),
			new this.latlng( 28.558259, -81.378294 ),
			new this.latlng( 28.557762, -81.379037 ),
			new this.latlng( 28.557388, -81.379434 ),
			new this.latlng( 28.55678, -81.379978 ),
			new this.latlng( 28.556271, -81.380343 ),
			new this.latlng( 28.555475, -81.380695 ),
			new this.latlng( 28.554865, -81.380866 ),
			new this.latlng( 28.55445, -81.380925 ),
			new this.latlng( 28.553908, -81.380933 ),
			new this.latlng( 28.550982, -81.380909 ),
			new this.latlng( 28.54757, -81.38085 ),
			new this.latlng( 28.538621, -81.380754 ),
			new this.latlng( 28.537877, -81.380807 ),
			new this.latlng( 28.537161, -81.381097 ),
			new this.latlng( 28.536463, -81.381676 ),
			new this.latlng( 28.535709, -81.382331 ),
			new this.latlng( 28.535219, -81.382631 ),
			new this.latlng( 28.534616, -81.382749 ),
			new this.latlng( 28.534173, -81.382749 ),
			new this.latlng( 28.530784, -81.382416 ),
			new this.latlng( 28.52633, -81.381939 ),
			new this.latlng( 28.523446, -81.381649 ),
			new this.latlng( 28.520212, -81.381365 ),
			new this.latlng( 28.517384, -81.381108 ),
			new this.latlng( 28.514386, -81.380796 ),
			new this.latlng( 28.513863, -81.380737 ),
			new this.latlng( 28.511341, -81.380142 ),
			new this.latlng( 28.508513, -81.379455 ),
			new this.latlng( 28.505317, -81.378677 ),
			new this.latlng( 28.502281, -81.377937 ),
			new this.latlng( 28.498095, -81.376918 ),
			new this.latlng( 28.49503, -81.376172 ),
			new this.latlng( 28.491862, -81.375405 ),
			new this.latlng( 28.488859, -81.374676 ),
			new this.latlng( 28.48578, -81.373807 ),
			new this.latlng( 28.482748, -81.372943 ),
			new this.latlng( 28.48023, -81.372235 ),
			new this.latlng( 28.477665, -81.3715 ),
			new this.latlng( 28.474581, -81.370625 ),
			new this.latlng( 28.471959, -81.369885 ),
			new this.latlng( 28.469677, -81.369236 ),
			new this.latlng( 28.466626, -81.368372 ),
			new this.latlng( 28.463961, -81.367616 ),
			new this.latlng( 28.462386, -81.367214 ),
			new this.latlng( 28.460759, -81.36701 ),
			new this.latlng( 28.460221, -81.366972 ),
			new this.latlng( 28.453251, -81.366978 ),
			new this.latlng( 28.44626, -81.36692 ),
			new this.latlng( 28.44175, -81.36684 ),
			new this.latlng( 28.43977, -81.36718 ),
			new this.latlng( 28.43743, -81.36783 ),
			new this.latlng( 28.41318, -81.37531 ),
			new this.latlng( 28.41067, -81.37581 ),
			new this.latlng( 28.40067, -81.37504 ),
			new this.latlng( 28.39251, -81.37443 ),
			new this.latlng( 28.37086, -81.3728 ),
			new this.latlng( 28.3689, -81.37298 ),
			new this.latlng( 28.36648, -81.37401 ),
			new this.latlng( 28.36323, -81.37684 ),
			new this.latlng( 28.35147, -81.38735 ),
			new this.latlng( 28.35007, -81.38817 ),
			new this.latlng( 28.3468, -81.38909 ),
			new this.latlng( 28.31964, -81.39574 ),
			new this.latlng( 28.30767, -81.39864 ),
			new this.latlng( 28.30126, -81.40021 ),
			new this.latlng( 28.29758, -81.40132 ),
			new this.latlng( 28.2955, -81.40252 ),
			new this.latlng( 28.29378, -81.40421 ),
			new this.latlng( 28.28951, -81.40868 ),
			new this.latlng( 28.28823, -81.41086 ),
			new this.latlng( 28.28062, -81.42473 ),
			new this.latlng( 28.27304, -81.43824 ),
			new this.latlng( 28.25928, -81.46296 ),
			new this.latlng( 28.25853, -81.46497 ),
			new this.latlng( 28.25822, -81.46838 ),
			new this.latlng( 28.25887, -81.48453 ),
		];
	}

	/**
	 * Centers the map to the current or default center point.
	 * If the map is not initialized, an error message is logged and the function returns.
	 *
	 * @return {void}
	 */
	reCenter() {
		if ( ! this.map ) {
			console.error( 'Map not initialized' ); // eslint-disable-line no-console
			return;
		}
		const centerPoint = this.currentCenterPoint || this.defaultCenterPoint;
		this.map.setCenter( centerPoint );
	}

	/**
	 * Resizes the map.
	 *
	 * @return {void}
	 */
	resizeMap() {
		if ( ! this.map ) {
			console.error( 'Map not defined' ); // eslint-disable-line no-console
			return;
		}

		google.maps.event.trigger( this.map, 'resize' );
		if ( this.isMobile() ) {
			this.adjustMobileView();
		} else {
			this.adjustDesktopView();
		}
	}

	/**
	 * Adjust the mobile view by setting the height of the map element to 100vh and closing trains and stations.
	 * @function
	 * @return {void}
	 */
	adjustMobileView() {
		this.mapElement.style.height = `100vh`;
		document.documentElement.style.overflowY = 'auto';
		this.handleCloseAction( 'trains' );
		this.handleCloseAction( 'stations' );
	}

	/**
	 * Adjusts the desktop view.
	 *
	 * @return {void}
	 */
	adjustDesktopView() {
		const selectedContent = this.queryElement( '.content.selected' );
		document.documentElement.style.overflowY = 'hidden';
		this.mapElement.style.height = window.innerHeight - this.siteHeader.getBoundingClientRect().height + 'px';
		this.lists.forEach(
			( list ) =>
				( list.style.maxHeight =
					window.innerHeight - this.siteHeader.getBoundingClientRect().height - 100 + 'px' )
		);

		if ( selectedContent ) {
			const selectedContentId = selectedContent.getAttribute( 'id' );
			if ( this.selectedTrain ) {
				this.activateTabAndContent( selectedContentId, this.selectedTrain );
			}
		}
	}

	/**
	 * Loads the stations data.
	 *
	 * @return {Promise<void>} - A promise that resolves when the stations data is loaded successfully, or rejects with an error.
	 */
	async loadStations() {
		try {
			const stationElements = this.queryAllElements( '#stations a.station' );
			const stationsData = Array.from( stationElements ).map( this.extractStationData );

			this.clearMarkers( this.markersStation );
			stationsData.forEach( ( station ) => {
				this.createStation( station );
			} );
		} catch ( error ) {
			console.error( 'Error loading stations:', error ); // eslint-disable-line no-console
		}
	}

	/**
	 * Extracts station data from the given HTML element.
	 *
	 * @param {HTMLElement} ele - The HTML element from which to extract the station data.
	 *
	 * @return {Object} - An object containing the extracted station data.
	 *                   The returned object has the following properties:
	 *                   - acf: An object containing additional station data.
	 *                          - station_latitude: The latitude of the station.
	 *                          - station_longitude: The longitude of the station.
	 *                          - station_address: The address of the station.
	 *                          - station_phone_number: The phone number of the station.
	 *                          - station_feed_id: The feed ID of the station.
	 *                   - title: An object containing the station title.
	 *                            - rendered: The rendered title of the station.
	 *                   - link: The link associated with the station.
	 *                   - letter: The letter associated with the station.
	 */
	extractStationData( ele ) {
		return {
			acf: {
				station_latitude: ele.getAttribute( 'data-lat' ),
				station_longitude: ele.getAttribute( 'data-lon' ),
				station_address: ele.getAttribute( 'data-address' ),
				station_phone_number: ele.getAttribute( 'data-phonenumber' ),
				station_feed_id: ele.getAttribute( 'data-stationid' ),
				station_dir_link: ele.getAttribute( 'data-directions' ),
			},
			title: {
				rendered: ele.getAttribute( 'data-name' ),
			},
			link: ele.getAttribute( 'data-link' ),
			letter: ele.getAttribute( 'data-letter' ),
		};
	}

	/**
	 * Creates a station using the provided station data.
	 *
	 * @param {Object} stationData                          - The data object representing the station.
	 * @param {string} stationData.acf.station_latitude     - The latitude of the station.
	 * @param {string} stationData.acf.station_longitude    - The longitude of the station.
	 * @param {string} stationData.title.rendered           - The name of the station.
	 * @param {string} stationData.link                     - The link to the station.
	 * @param {string} stationData.acf.station_address      - The address of the station.
	 * @param {string} stationData.acf.station_phone_number - The phone number of the station.
	 * @param {string} stationData.acf.station_feed_id      - The feed ID of the station.
	 * @param {string} stationData.letter                   - The letter associated with the station.
	 *
	 * @return {void} - This method does not return a value.
	 */
	createStation( stationData ) {
		const lat = parseFloat( stationData.acf.station_latitude );
		const lon = parseFloat( stationData.acf.station_longitude );
		const name = stationData.title.rendered;
		const link = stationData.link;
		const address = stationData.acf.station_address;
		const phoneNumber = stationData.acf.station_phone_number;
		const stationID = stationData.acf.station_feed_id;
		const letter = stationData.letter;
		const dirLink = stationData.acf.station_dir_link;
		const additionalData = {
			address,
			phoneNumber,
			link,
			dirLink,
		};
		this.createStationMarker( lat, lon, name, letter, additionalData, stationID );
	}

	/**
	 * Clears all markers from the map.
	 * @param {Array} markersArray - The array of markers to be cleared.
	 * @return {void}
	 */
	clearMarkers( markersArray ) {
		markersArray.forEach( ( marker ) => marker.setMap( null ) );
		markersArray.length = 0;
	}

	/**
	 * Creates a station marker on the Google Maps.
	 *
	 * @param {number}  lat       - The latitude of the station location.
	 * @param {number}  lon       - The longitude of the station location.
	 * @param {string}  name      - The name of the station.
	 * @param {string}  letter    - The letter representing the station.
	 * @param {boolean} whole     - Boolean representing if the station is a whole station.
	 * @param {number}  stationID - The ID of the station.
	 * @return {undefined}
	 */
	createStationMarker( lat, lon, name, letter, whole, stationID ) {
		const marker = new google.maps.Marker( {
			id: stationID,
			type: 'station',
			position: { lat, lng: lon },
			map: this.map,
			title: name,
			icon: {
				url: `/wp-content/themes/sunrail-theme/assets/icons/station-icons/station-marker-${ letter }.svg`,
				scaledSize: new google.maps.Size( 24, 24 ),
			},
		} );
		marker.addListener( 'click', () => {
			this.activateTabAndContent( 'stations', stationID );
		} );
		this.markersStation.push( marker );
	}

	/**
	 * Load trains from the train feed asynchronously.
	 * If the train data is empty or not available, the method will retry loading trains.
	 * Otherwise, it will clear the existing trains, prepare and update new train data,
	 * sort the train list, update the list items, and retry loading trains.
	 *
	 * @return {void}
	 */
	async loadTrains() {
		try {
			const trainData = await this.getTrainFeed();
			if ( ! trainData || trainData.length === 0 ) {
				this.retryLoadTrains();
				return;
			}

			this.clearExistingTrains();
			const newTrainData = this.prepareNewTrainData( trainData );
			this.updateTrainList( newTrainData );
			this.sortTrainList( this.trainListParent );
			this.updateListItems();
			this.retryLoadTrains();
		} catch ( error ) {
			console.error( 'Error loading trains:', error ); // eslint-disable-line no-console
			this.retryLoadTrains();
		}
	}

	/**
	 * Fetches the train feed data from the specified endpoint and transforms the data.
	 *
	 * @return {Promise<Array>} - A promise that resolves with the transformed train feed data.
	 */
	async getTrainFeed() {
		try {
			const response = await fetch( '/wp-json/sunrail/v1/train_feed', {
				headers: {
					'X-WP-Nonce': window.wpApiSettings.nonce,
				},
			} );
			if ( ! response.ok ) {
				throw new Error( `HTTP error! status: ${ response.status }` );
			}
			const data = await response.json();
			return this.transformTrainData( data );
		} catch ( error ) {
			console.error( 'Error fetching train feed:', error ); // eslint-disable-line no-console
			return [];
		}
	}

	/**
	 * Transforms train data by manipulating certain properties.
	 * @param {Array} data - The train data to be transformed.
	 * @return {Array} - The transformed train data.
	 */
	transformTrainData( data ) {
		return data.map( ( train ) => ( {
			...train,
			Delay: Math.round( train.Delay / ( 60 * 1000 ) ),
			DelayText: train.Delay === 0 ? '' : `${ train.Delay } mins`,
			Dir: train.Direction,
			Direction: train.Direction === 'S' ? 'South' : 'North',
			DistanceToNextStation:
				train.DistanceToNextStation === 0
					? 'At station'
					: `${ Math.round( train.DistanceToNextStation ) } miles`,
			MPH: `${ Math.round( train.Speed * 0.621371 ) }`,
			KPH: `${ Math.round( train.Speed ) }`,
			friendly: train.DelayFlag ? 'Delayed' : `${ train.ArrivalTime }${ ! train.RealtimeFlag ? '*' : '' }`,
		} ) );
	}

	/**
	 * Creates a train marker on the map.
	 *
	 * @param {number} lat       - The latitude of the train's position.
	 * @param {number} lon       - The longitude of the train's position.
	 * @param {string} name      - The name of the train.
	 * @param {number} number    - The train number.
	 * @param {Object} whole     - The complete train data.
	 * @param {string} trainID   - The unique identifier of the train.
	 * @param {string} direction - The direction of the train ('N' for northbound, 'S' for southbound).
	 */
	createTrain( lat, lon, name, number, whole, trainID, direction ) {
		const pointY = direction === 'N' ? 15 : 23;
		const directionText = direction === 'N' ? 'northbound' : 'southbound';
		const trainNumberSlug = trainID.toLowerCase();
		const markerType = whole.DelayFlag ? 'delay' : 'default';
		const selectedClass = this.selectedTrain === trainID ? '-selected' : '';
		const trainMarker = new google.maps.Marker( {
			id: trainID,
			type: 'train',
			position: { lat, lng: lon },
			map: this.map,
			icon: `/wp-content/themes/sunrail-theme/assets/icons/train-markers/${ directionText }/marker-${ trainNumberSlug }-${ markerType }${ selectedClass }.svg`,
			draggable: false,
			raiseOnDrag: false,
			labelAnchor: new google.maps.Point( 10, pointY ),
			labelClass: 'mapIconLabel',
			labelInBackground: false,
			trainData: whole,
			title: trainID + direction,
			zIndex: 300,
		} );
		trainMarker.addListener( 'click', () => {
			this.activateTabAndContent( 'trains', trainID );
		} );
		this.markersTrain.push( trainMarker );
		this.markersTrainByID[ trainID ] = trainMarker;
	}

	/**
	 * Removes all existing train markers from the map.
	 *
	 * @return {void}
	 */
	clearExistingTrains() {
		this.markersTrain.forEach( ( marker ) => marker.setMap( null ) );
		this.markersTrain.length = 0;
		if ( this.trainsNotScheduled() ) {
			this.trainsClosed.style.display = 'block';
			this.trainsOpened.style.display = 'none';
			this.trainMenuHeaderText.innerHTML = this.trainMenuHeaderText.getAttribute( 'data-trains-closed' );
			this.trainsContent.classList.add( 'closed' );
		} else {
			this.trainsClosed.style.display = 'none';
			this.trainsOpened.style.display = 'block';
			this.trainMenuHeaderText.innerHTML = this.trainMenuHeaderText.getAttribute( 'data-trains-scheduled' );
			this.trainsContent.classList.remove( 'closed' );
		}
	}

	/**
	 * Determines if trains are not scheduled based on current time and day of the week.
	 * @return {boolean} - True if trains are not scheduled, false otherwise.
	 */
	trainsNotScheduled() {
		// Get current Unix timestamp in UTC.
		const utcTimestamp = Math.floor( Date.now() / 1000 );

		// Adjust for WordPress site timezone.
		// Note: You need to provide the GMT offset value for your WordPress site.
		// For example, if your WordPress site is set to GMT-5, use -5 * 60 * 60.
		const timezoneOffset = parseInt( window.wpApiSettings.tz ) * 60 * 60;
		const localTimestamp = utcTimestamp + timezoneOffset;

		// Create a Date object for local time.
		const localDate = new Date( localTimestamp * 1000 );

		// Get current hour, minutes, and day of the week.
		const currentHour = localDate.getUTCHours();
		const currentMinute = localDate.getUTCMinutes();
		const currentDayOfWeek = localDate.getUTCDay() === 0 ? 7 : localDate.getUTCDay(); // In JS, Sunday is 0, converting it to 7.

		// Calculate total minutes since midnight.
		const totalMinutes = currentHour * 60 + currentMinute;

		// Define time thresholds.
		const time2330 = 23 * 60 + 30;
		const time0500 = 5 * 60;

		// Check conditions.
		const isAfter2330 = totalMinutes > time2330;
		const isAfter0500 = totalMinutes > time0500;

		// Determine if trains are not scheduled.
		return (
			( currentDayOfWeek === 6 && isAfter2330 ) || // Saturday after 23:30.
			currentDayOfWeek === 7 || // All day Sunday.
			( currentDayOfWeek === 1 && ! isAfter0500 )
		); // Monday before 05:00.
	}

	/**
	 * Prepares new train data.
	 *
	 * @param {Array} data - The array of train data.
	 * @return {Map} - The map containing prepared train data.
	 */
	prepareNewTrainData( data ) {
		const newTrainData = new Map();
		data.forEach( ( train, index ) => {
			this.createTrain(
				train.Lat,
				train.Long,
				train.Name,
				index + 1,
				train,
				train.TripId,
				train.Dir,
				train.NextStation
			);
			const delayedClass = train.DelayFlag ? ' delayed' : '';
			newTrainData.set( train.TripId, this.trainMarkup( train, delayedClass ) );
		} );
		return newTrainData;
	}

	/**
	 * Returns the HTML markup for a train.
	 *
	 * @param {Object} train        - The train object containing TripId and Dir properties.
	 * @param {string} delayedClass - The class name to apply if the train is delayed.
	 * @return {string} The HTML markup for the train.
	 */
	trainMarkup( train, delayedClass ) {
		return `<a href="javascript:void(0);" class="train" data-trainid="${ train.TripId }">
                    <span class="schedule${ delayedClass }">
                        <strong class="schedule-title">${ train.TripId }</strong> 
                        ${ train.Dir === 'N' ? 'Northbound' : 'Southbound' }
                    </span>
                </a>`;
	}

	/**
	 * Update the train list with new train data.
	 *
	 * @param {Array} newTrainData - The new train data to update the list with.
	 *
	 * @return {void}
	 */
	updateTrainList( newTrainData ) {
		this.removeNonExistingTrains( newTrainData );
		this.addOrUpdateTrains( newTrainData );
	}

	/**
	 * Removes non-existing trains from the train list based on the provided new train data
	 *
	 * @param {Set} newTrainData - A set containing the train IDs of the new trains
	 *
	 * @return {void} - This method does not return any value
	 */
	removeNonExistingTrains( newTrainData ) {
		const existingTrains = this.trainListParent.querySelectorAll( '.train' );
		existingTrains.forEach( ( train ) => {
			if ( ! newTrainData.has( train.dataset.trainid ) ) {
				train.remove();
			}
		} );
	}

	/**
	 * Adds or updates trains based on the provided train data.
	 *
	 * @param {Map} newTrainData - A map of train IDs and corresponding markup.
	 */
	addOrUpdateTrains( newTrainData ) {
		newTrainData.forEach( ( markup, trainId ) => {
			let trainElement = this.trainListParent.querySelector( `.train[data-trainid="${ trainId }"]` );
			if ( ! trainElement ) {
				trainElement = document.createElement( 'div' );
				trainElement.innerHTML = markup;
				this.trainListParent.appendChild( trainElement.firstChild );
			}
		} );
	}

	/**
	 * Updates list items in a list.
	 *
	 * @return {void}
	 */
	updateListItems() {
		this.listItems = this.queryAllElements( '.list .station, .list .train' );
		this.setListItemClickListeners();
	}

	/**
	 * Retries to load trains after a timeout of 15 seconds.
	 *
	 * @function retryLoadTrains
	 */
	retryLoadTrains() {
		setTimeout( () => this.loadTrains(), 15000 );
	}

	/**
	 * Sorts the train list based on the train id.
	 *
	 * @param {HTMLElement} listParent - The parent element containing the train list.
	 *
	 * @return {void}
	 */
	sortTrainList( listParent ) {
		const trainsArray = Array.from( listParent.querySelectorAll( '.train' ) );
		trainsArray.sort( ( a, b ) =>
			a.dataset.trainid.localeCompare( b.dataset.trainid, undefined, { numeric: true, sensitivity: 'base' } )
		);
		trainsArray.forEach( ( train ) => listParent.appendChild( train ) );
	}

	/**
	 * Activates a tab and its corresponding content.
	 *
	 * @see This is used when the markers are clicked or the map is resized.
	 *
	 * @param {string} targetId - The ID of the tab to activate.
	 * @param {string} itemId   - The ID of the item to handle the click for.
	 * @return {void}
	 */
	activateTabAndContent( targetId, itemId ) {
		// Deactivate all tabs and contents
		this.tabs.forEach( ( tab ) => tab.classList.remove( 'active' ) );
		this.contents.forEach( ( content ) => content.classList.remove( 'active' ) );

		// Activate the specific tab and content
		const target = this.getElement( targetId );
		this.activateTab( targetId );
		if ( target ) target.classList.add( 'active' );

		// Handle the item click with the provided itemId
		this.handleItemClick( null, targetId, itemId );
	}

	/**
	 * Sets click listeners for each tab.
	 */
	setTabClickListeners() {
		this.tabs.forEach( ( tab, index ) => {
			tab.addEventListener( 'click', () => this.handleTabClick( tab ) );

			// Handle keydown for keyboard navigation
			tab.addEventListener( 'keydown', ( e ) => {
				// Define the keys to listen for
				const keyLeftArrow = 37;
				const keyRightArrow = 39;
				const keyEnter = 13;
				const keySpace = 32;

				let newIndex = index;

				// Navigate left or right
				if ( e.keyCode === keyRightArrow ) {
					newIndex = index === this.tabs.length - 1 ? 0 : index + 1;
				} else if ( e.keyCode === keyLeftArrow ) {
					newIndex = index === 0 ? this.tabs.length - 1 : index - 1;
				}

				// Activate new tab and move focus
				if ( newIndex !== index ) {
					e.preventDefault(); // Prevent scrolling
					this.tabs[ newIndex ].focus();
					this.handleTabClick( this.tabs[ newIndex ] );
				}

				// Select tab with Enter or Space
				if ( e.keyCode === keyEnter || e.keyCode === keySpace ) {
					e.preventDefault(); // Prevent scroll on Space
					this.handleTabClick( tab );
				}
			} );
		} );
	}

	/**
	 * Handles the click event on a tab element.
	 *
	 * @param {HTMLElement} tab - The tab element clicked.
	 * @return {void}
	 */
	handleTabClick( tab ) {
		const targetId = tab.getAttribute( 'data-target' );
		const targetPanel = this.getElement( targetId );

		// Hide all details if not on mobile
		if ( ! this.isMobile() ) {
			this.hideDetailsDiv();
		}

		// Activate the clicked tab and its corresponding content
		this.activateTab( targetId );
		this.activateContent( targetPanel );
	}

	/**
	 * Activates a specific tab based on the targetId.
	 *
	 * @param {string} targetId - The id of the tab to be activated.
	 */
	activateTab( targetId ) {
		this.tabs.forEach( ( tab ) => {
			if ( tab.getAttribute( 'data-target' ) === targetId ) {
				tab.classList.add( 'active' );
				tab.setAttribute( 'tabindex', '0' ); // Make active tab focusable
			} else {
				tab.classList.remove( 'active' );
				tab.setAttribute( 'tabindex', '-1' ); // Make inactive tabs not focusable
			}
		} );
	}

	/**
	 * Activates the given content by adding the 'active' CSS class to the element.
	 * Also, removes the 'active' class from all other contents.
	 *
	 * @param {HTMLElement} content - The content element to activate.
	 * @return {void}
	 */
	activateContent( content ) {
		this.contents.forEach( ( c ) => {
			c.classList.remove( 'active' ); // Removes 'active' class from all contents
			c.setAttribute( 'hidden', true ); // Hide all tab panels
		} );
		if ( content ) {
			content.classList.add( 'active' ); // Add 'active' class to the target content
			content.removeAttribute( 'hidden' ); // Show the target content panel
		}
	}

	/**
	 * Adds the 'selected' class to the specified content element and the menu element, and removes the 'selected' class from all other content elements.
	 *
	 * @param {HTMLElement} content - The content element to be selected. If null, the 'selected' class will be removed from all content elements.
	 *
	 * @return {void}
	 */
	addContentSelectedClass( content = null ) {
		this.contents.forEach( ( c ) => c.classList.remove( 'selected' ) );
		this.menu.classList.remove( 'selected' );
		this.userButtons.style.bottom = null;
		if ( content ) {
			content.classList.add( 'selected' );
			this.menu.classList.add( 'selected' );
			if ( this.isMobile() ) {
				// adds open details height to the bottom style element
				this.userButtons.style.bottom = `${ this.queryElement( '.details.active' ).offsetHeight + 10 }px`;
			}
		}
	}

	/**
	 * Sets click listeners for list items.
	 *
	 * @return {void}
	 */
	setListItemClickListeners() {
		this.listItems.forEach( ( item ) => {
			// Check if the event listener has already been added
			if ( ! item.dataset.listenerAdded ) {
				item.addEventListener( 'click', () => this.handleItemClick( item ) );

				// Mark the item to indicate that the event listener has been added
				item.dataset.listenerAdded = 'true';
			}
		} );
	}

	/**
	 * Handles item click event.
	 *
	 * @param {HTMLElement} item          - The item clicked.
	 * @param {string}      [listId=null] - The ID of the list containing the item (Optional).
	 * @param {string}      [ID=null]     - The ID of the clicked item (Optional).
	 * @return {void}
	 */
	handleItemClick( item, listId = null, ID = null ) {
		// If the item is null, use the provided listId and ID
		if ( ! item ) {
			if ( ! listId || ! ID ) {
				console.error( 'Invalid arguments for handleItemClick when item is null.' ); // eslint-disable-line no-console
				return;
			}
			item = this.findListItemById( listId, ID );
		} else {
			// If the item is provided, derive listId and ID from the item
			listId = item.parentElement.parentElement.parentElement.getAttribute( 'id' );
			ID = item.getAttribute( `data-${ listId === 'stations' ? 'stationid' : 'trainid' }` );
		}

		this.resetActiveItems( listId );
		this.setActiveItem( item, listId, ID );
	}

	/**
	 * Finds a list item by ID.
	 *
	 * @param {string}        listId - The ID of the list.
	 * @param {string|number} ID     - The ID of the list item to find.
	 * @return {Element|null} - The found list item element or null if not found.
	 */
	findListItemById( listId, ID ) {
		return this.queryElement(
			`#${ listId } .${ listId === 'stations' ? 'station' : 'train' }[data-${
				listId === 'stations' ? 'stationid' : 'trainid'
			}="${ ID }"]`
		);
	}

	/**
	 * Resets the active items in a given list by removing the 'active' class from each item.
	 * Also resets the marker size for each item in the list.
	 * Sets the selected train to null, removes the 'content-selected' class, and hides the details div.
	 *
	 * @param {string} listId - The ID of the list containing the items to be reset.
	 * @return {void}
	 */
	resetActiveItems( listId ) {
		this.listItems.forEach( ( i ) => {
			i.classList.remove( 'active' );
			this.resetMarkerSize( i, listId );
		} );
		this.selectedTrain = null;
		this.addContentSelectedClass( null );
		this.hideDetailsDiv();
	}

	/**
	 * Sets the given item as active in the list and performs necessary actions
	 * to update the UI.
	 *
	 * @param {HTMLElement} item   - The item to set as active.
	 * @param {string}      listId - The ID of the list containing the item.
	 * @param {string}      ID     - The ID of the active item.
	 * @return {void}
	 */
	setActiveItem( item, listId, ID ) {
		const content = this.getElement( listId );
		this.selectedTrain = ID;
		item.classList.add( 'active' );
		this.currentCenterPoint = this.getMarker( item ).getPosition();
		this.reCenter();
		this.updateDetails( item, listId );
		this.showDetailsDiv( listId, item );
		this.addContentSelectedClass( content );
		this.enlargeMarker( ID, listId );
		if ( this.isMobile() ) {
			this.handleCloseAction( 'trains' );
			this.handleCloseAction( 'stations' );
		}
	}

	/**
	 * Resets the size of a marker.
	 *
	 * @param {Object} item - The item to reset the marker size for.
	 */
	resetMarkerSize( item ) {
		const marker = this.getMarker( item );
		if ( marker ) {
			this.updateMarkerSize( marker, marker.type === 'station' ? 24 : null );
		}
	}

	/**
	 * Enlarges a marker based on its ID and list ID.
	 *
	 * @param {string} ID     - The ID of the marker.
	 * @param {string} listId - The ID of the list containing the marker.
	 *
	 * @return {void} - This method does not return any value.
	 */
	enlargeMarker( ID, listId ) {
		const marker = this.findMarkerById( ID, listId === 'stations' ? this.markersStation : this.markersTrain );
		if ( marker ) {
			this.updateMarkerSize( marker, marker.type === 'station' ? 36 : 'selected' );
		}
	}

	/**
	 * Updates the size of a marker on a map.
	 *
	 * @typedef {Object} Marker
	 * @param    {Marker}             marker - The marker to update.
	 * @param    {number}             size   - The new size of the marker.
	 * @return {void}
	 * @property {google.maps.Marker} marker - The marker object to update.
	 */
	updateMarkerSize( marker, size ) {
		const icon = this.getMarkerIcon( marker );
		if ( icon ) {
			this.setMarkerIcon( marker, icon, size );
		} else {
			console.error( 'Marker icon is not properly formatted' ); // eslint-disable-line no-console
		}
	}

	/**
	 * Retrieves the icon for a given marker.
	 *
	 * @typedef {Object} Marker
	 * @param    {Marker}             marker - The marker object from which to retrieve the icon.
	 * @return {Object} - The icon object containing the URL of the icon.
	 * @property {google.maps.Marker} marker - The marker object to retrieve the icon from.
	 */
	getMarkerIcon( marker ) {
		const icon = marker.getIcon();
		return typeof icon === 'string' ? { url: icon } : icon;
	}

	/**
	 * Sets the marker icon of a given marker.
	 *
	 * @param {google.maps.Marker} marker - The marker object.
	 * @param {Object}             icon   - The icon object that contains the URL of the icon.
	 * @param {number|string}      size   - The size of the icon.
	 *                                    - If specified, the marker icon will be scaled to the given size.
	 *                                    - If set to 'selected', the marker icon URL will be replaced with the selected version of the icon URL.
	 *                                    - If not specified or set to anything other than 'selected', the marker icon URL will be replaced with the default version of the icon URL.
	 *
	 * @return {void}
	 */
	setMarkerIcon( marker, icon, size ) {
		if ( size && size !== 'selected' ) {
			marker.setIcon( { url: icon.url, scaledSize: new google.maps.Size( size, size ) } );
		} else if ( size === 'selected' ) {
			marker.setIcon( { url: icon.url.replace( '.svg', '-selected.svg' ), map: this.map } );
		} else {
			marker.setIcon( { url: icon.url.replace( '-selected.svg', '.svg' ), map: this.map } );
		}
	}

	/**
	 * Finds a marker by its ID in an array of markers.
	 *
	 * @param {number|string} ID           - The ID of the marker to find.
	 * @param {Array}         markersArray - The array of markers to search in.
	 * @return {Object} - The marker object if found, or undefined if not found.
	 */
	findMarkerById( ID, markersArray ) {
		return markersArray.find( ( marker ) => marker.id === ID );
	}

	/**
	 * Hides the details divs by removing the 'active' class from each element in the detailDivs array.
	 *
	 * @return {undefined}
	 */
	hideDetailsDiv() {
		this.detailDivs.forEach( ( element ) => {
			element.classList.remove( 'active' ); // Remove the 'active' class
			element.setAttribute( 'hidden', '' ); // Set the 'hidden' attribute to hide it
			element.removeAttribute( 'tabindex' ); // Remove the 'tabindex' attribute to make it unfocusable
		} );
	}

	/**
	 * Shows the details div for a given list ID.
	 *
	 * @param {string} listId - The ID of the list for which to show the details div.
	 * @return {void}
	 */
	showDetailsDiv( listId ) {
		const currentDetailsDiv = this.getElement( `${ listId }-details` );
		if ( currentDetailsDiv ) {
			// Clear any inline visibility styles that might have been previously set
			currentDetailsDiv.style.visibility = '';

			// Remove 'hidden' attribute to make it accessible again
			currentDetailsDiv.removeAttribute( 'hidden' );

			// Add 'active' class to make it visually active
			currentDetailsDiv.classList.add( 'active' );

			// Make div focusable for keyboard navigation
			currentDetailsDiv.setAttribute( 'tabindex', '0' );
			currentDetailsDiv.focus();

			// Adjust position based on whether it's in mobile view
			if ( this.isMobile() ) {
				// For mobile, adjust positioning to be just within viewport
				currentDetailsDiv.style.bottom = 'auto';
				currentDetailsDiv.style.top = `${ window.innerHeight - currentDetailsDiv.offsetHeight - 10 }px`;
			} else {
				// For non-mobile, reset any specific positioning to use default styling
				currentDetailsDiv.style.top = '';
				currentDetailsDiv.style.bottom = '';
			}
		}
	}

	/**
	 * Update the details of an item based on the provided list ID.
	 * @param {HTMLElement} item   - The item element containing the dataset.
	 * @param {string}      listId - The ID of the list (e.g., 'stations', 'trains').
	 * @return {void}
	 */
	updateDetails( item, listId ) {
		const detailsData = item.dataset;
		if ( listId === 'stations' ) {
			this.populateStationDetailsDiv( detailsData );
		} else if ( listId === 'trains' ) {
			const trainId = detailsData.trainid;
			const trainMarker = this.markersTrainByID[ trainId ];
			this.populateTrainDetailsDiv( trainMarker.trainData );
		}
	}

	/**
	 * Populates the station details div with the provided station data.
	 *
	 * @param {Object} stationData - The data of the station.
	 */
	populateStationDetailsDiv( stationData ) {
		const detailsDiv = this.getElement( 'stations-details' );
		this.updateStationDetailsDiv( detailsDiv, stationData );
	}

	/**
	 * Updates the station details div with the given station data.
	 *
	 * @param {HTMLElement} detailsDiv  - The div element containing the station details.
	 * @param {Object}      stationData - The data of the station.
	 * @return {void}
	 */
	updateStationDetailsDiv( detailsDiv, stationData ) {
		if ( typeof window.fetchAndUpdateStatus === 'function' ) {
			window.fetchAndUpdateStatus();
		} else {
			console.log( 'fetchAndUpdateStatus function does not exist' ); // eslint-disable-line no-console
		}
		const stationIcon = detailsDiv.querySelector( '.station-icon' );
		const img = stationIcon.querySelector( 'img' ) || stationIcon.appendChild( document.createElement( 'img' ) );
		img.src = `/wp-content/themes/sunrail-theme/assets/icons/station-icons/station-marker-${ stationData.letter }.svg`;
		detailsDiv.querySelector( '.station-title' ).textContent = stationData.name;
		detailsDiv.querySelector( '.station-title' ).href = stationData.link;
		detailsDiv.querySelector( '.station-directions' ).href = stationData.directions;
		detailsDiv.querySelector( '.station-address' ).innerHTML = this.htmlDecode( stationData.address );
		detailsDiv.querySelector( '#nextTrain' ).setAttribute( 'data-feed-id', stationData.stationid );
	}

	/**
	 * Decodes HTML-encoded text.
	 *
	 * @param {string} input - The HTML-encoded text to be decoded.
	 * @return {string} - The decoded text.
	 */
	htmlDecode( input ) {
		const doc = new DOMParser().parseFromString( input, 'text/html' );
		return doc.documentElement.textContent;
	}

	/**
	 * Populates the train details section with the specified train data.
	 *
	 * @param {Object} trainData - The train data to populate the details section with.
	 */
	populateTrainDetailsDiv( trainData ) {
		const detailsDiv = this.getElement( 'trains-details' );
		this.updateTrainDetailsDiv( detailsDiv, trainData );
	}

	/**
	 * Updates the train details div with the given train data.
	 *
	 * @param {HTMLElement} detailsDiv - The div element to update with train details.
	 * @param {Object}      trainData  - The train data to display in the details div.
	 * @return {void}
	 */
	updateTrainDetailsDiv( detailsDiv, trainData ) {
		const delayClass = trainData.DelayFlag ? 'delayed' : '';
		const trainTitleElement = detailsDiv.querySelector( '.train-title' );
		const trainSpeedMphElement = detailsDiv.querySelector( '.train-speed-mph' );
		const trainSpeedKphElement = detailsDiv.querySelector( '.train-speed-kph' );
		const nextStationElement = detailsDiv.querySelector( '.station-title' );
		const arrivalTimeElement = detailsDiv.querySelector( '.station-time' );

		if ( delayClass !== '' ) {
			detailsDiv.classList.add( delayClass );
		}

		trainTitleElement.textContent = `${ trainData.TripId } ${ trainData.Dir === 'N' ? 'NB' : 'SB' }`;
		trainSpeedMphElement.textContent = trainData.MPH + ' MPH';
		trainSpeedKphElement.textContent = trainData.KPH + ' KPH';
		nextStationElement.textContent = trainData.NextStationName;
		nextStationElement.href = `javascript:void(0);`;
		arrivalTimeElement.textContent = trainData.friendly;

		nextStationElement.addEventListener( 'click', () => {
			this.handleTabStationClick( trainData.NextStation );
		} );
	}

	/**
	 * Retrieves the marker object associated with the given item.
	 *
	 * @param {HTMLElement} item - The item element for which to retrieve the marker.
	 * @return {Object} - The marker object found in either the station markers or train markers.
	 */
	getMarker( item ) {
		const storage = item.classList.contains( 'station' ) ? this.markersStation : this.markersTrain;
		const dataId = item.classList.contains( 'station' ) ? 'stationid' : 'trainid';
		return storage.find( ( marker ) => marker.id === item.getAttribute( `data-${ dataId }` ) );
	}

	/**
	 * Handles the click event for the Next Station Link inside train details.
	 *
	 * @param {string} nextStation - The next station to be handled.
	 * @return {void}
	 */
	handleTabStationClick( nextStation ) {
		const target = this.queryElement( '#stations' );
		this.activateTab( 'stations' );
		this.activateContent( target );
		this.handleItemClick( null, 'stations', nextStation );
	}

	/**
	 * Sets the click listener for the close button of the stations.
	 *
	 * The close button click listener is responsible for handling the click event
	 * on the close button of the stations and triggering the closeStations method.
	 *
	 * @return {void}
	 */
	setStationsCloseClickListener() {
		this.stationClose.addEventListener( 'click', this.closeStations.bind( this ) );
	}

	/**
	 * Closes the stations.
	 *
	 * @return {void} - This method has no return value.
	 */
	closeStations() {
		this.handleCloseAction( 'stations' );
	}

	/**
	 * Sets the click listener for the trains close button.
	 *
	 * @return {void}
	 */
	setTrainsCloseClickListener() {
		this.trainClose.addEventListener( 'click', this.closeTrains.bind( this ) );
	}

	/**
	 * Closes the trains.
	 *
	 * @return {void}
	 */
	closeTrains() {
		this.handleCloseAction( 'trains' );
	}

	/**
	 * Closes the specified details by removing the 'active' class from the corresponding elements.
	 * Also resets the active items and re-centers the element.
	 *
	 * @param {string}  detailType - The type of details to be closed.
	 * @param {boolean} skipFocus  - Whether to skip focusing on the element after closing it.
	 * @return {void}
	 */
	closeDetails( detailType, skipFocus = false ) {
		const detailSelector = `a.${ detailType }`;
		this.detailDivs.forEach( ( element ) => element.classList.remove( 'active' ) );
		this.queryAllElements( detailSelector ).forEach( ( element ) => element.classList.remove( 'active' ) );
		this.resetActiveItems( detailType );

		if ( ! skipFocus ) {
			this.getElement( `${ detailType }s` ).focus();
		}
	}

	/**
	 * Sets a click event listener for the close button of a details type.
	 *
	 * @param {string} detailType - The type of details.
	 */
	setDetailsCloseClickListener( detailType ) {
		const closeButton = this[ `${ detailType }DetailsClose` ];

		if ( closeButton ) {
			closeButton.addEventListener( 'click', () => {
				this.closeDetails( detailType );
			} );
		}
	}

	/**
	 * Handles the action to close an element.
	 * Removes the 'active' class from the specified element and its descendants.
	 * If not in mobile view, also removes the 'active' class from tab elements of the specified element.
	 * Additionally, if not in mobile view, calls the closeDetails method to close any details associated with the specified element.
	 *
	 * @param {string} elementId - The ID of the element to be closed.
	 */
	handleCloseAction( elementId ) {
		const elements = this.getElement( elementId );
		if ( elements ) {
			// Remove 'active' class from the target element
			elements.classList.remove( 'active' );

			// Add 'hidden' attribute to improve accessibility
			elements.setAttribute( 'hidden', '' );

			// If not on mobile, additionally remove 'active' class from all descendant elements
			if ( ! this.isMobile() ) {
				const descendants = elements.querySelectorAll( '*' );
				descendants.forEach( ( descendant ) => {
					descendant.classList.remove( 'active' );
				} );
			}
		}

		// Remove 'active' class from all related tab elements
		const tabElements = this.queryAllElements( `.tab.${ elementId }` );
		if ( tabElements ) {
			tabElements.forEach( ( tabElement ) => {
				tabElement.classList.remove( 'active' );
				tabElement.focus();
			} );
		}

		// Close details if not on mobile
		if ( ! this.isMobile() ) {
			this.closeDetails( elementId, true );
		}
	}

	/**
	 * Sets a click listener on the footer icon.
	 *
	 * @return {void}
	 */
	setFooterIconClickListener() {
		this.footerIcon.addEventListener( 'click', () => this.toggleFooter() );
	}

	/**
	 * Sets a click listener for the site footer.
	 *
	 * @return {void}
	 */
	setFooterClickListener() {
		this.siteFooter.addEventListener( 'click', ( event ) => this.handleFooterClick( event ) );
	}

	/**
	 * Toggles the visibility of the site footer and adjusts the main div height accordingly.
	 *
	 * @return {void}
	 */
	toggleFooter() {
		const isActive = this.siteFooter.classList.toggle( 'active' );
		this.adjustMainDivHeight( isActive );
		this.mainDiv.classList.toggle( 'footerActive' );

		if ( isActive ) {
			this.addOverlay();
		} else {
			this.removeOverlay();
		}
	}

	/**
	 * Adjusts the height of the main div based on the provided isActive parameter.
	 * If isActive is true, the height will be calculated based on the screen height and the heights of the site header and footer.
	 * If isActive is false, the main div height will revert to its original value.
	 *
	 * @param {boolean} isActive - Indicates whether to adjust the main div height or revert it to its original value.
	 * @return {void}
	 */
	adjustMainDivHeight( isActive ) {
		let height = this.siteFooter.offsetHeight;
		if ( ! this.isMobile() ) {
			height += this.siteHeader.offsetHeight;
		}

		const screenHeight = window.innerHeight;
		if ( isActive ) {
			if ( this.isMobile() ) {
				this.mainDiv.style.height = '0px';
			} else if ( screenHeight > height ) {
				this.mainDiv.style.height = `${ screenHeight - height }px`;
			} else {
				this.mainDiv.style.height = `${ screenHeight - screenHeight * 0.8 }px`;
			}
		} else if ( ! this.isMobile() ) {
			this.mainDiv.style.height = window.innerHeight - this.siteHeader.getBoundingClientRect().height + 'px';
		} else {
			this.mainDiv.style.height = `100vh`;
		}
	}

	/**
	 * Adds an overlay element before the site header and attaches a click event listener to close it.
	 */
	addOverlay() {
		const overlay = document.createElement( 'div' );
		overlay.classList.add( 'overlay' );
		this.siteHeader.insertAdjacentElement( 'beforebegin', overlay );
		overlay.addEventListener( 'click', () => this.footerIcon.dispatchEvent( new Event( 'click' ) ) );
	}

	/**
	 * Removes the overlay element from the DOM.
	 *
	 * @return {void}
	 */
	removeOverlay() {
		const overlay = this.queryElement( '.overlay' );
		if ( overlay ) {
			overlay.remove();
		}
	}

	/**
	 * Handles the click event on the footer.
	 *
	 * @param {Event} event - The click event object.
	 * @return {void}
	 */
	handleFooterClick( event ) {
		if ( this.isClickOnCloseButton( event ) ) {
			this.toggleFooter();
		}
	}

	/**
	 * Checks if the click event occurred on the close button.
	 *
	 * @param {MouseEvent} event - The click event.
	 * @return {boolean} - True if the click event occurred on the close button, false otherwise.
	 */
	isClickOnCloseButton( event ) {
		const closeButtonSize = 24; // Size of the close button
		const closeButtonOffset = 12; // Offset from top and right

		const { top, right } = this.siteFooter.getBoundingClientRect();
		const isWithinHorizontalBounds =
			event.clientX >= right - closeButtonSize - closeButtonOffset && event.clientX <= right - closeButtonOffset;
		const isWithinVerticalBounds =
			event.clientY >= top + closeButtonOffset && event.clientY <= top + closeButtonSize + closeButtonOffset;

		return isWithinHorizontalBounds && isWithinVerticalBounds;
	}

	/**
	 * Checks if the current device is a mobile device.
	 *
	 * @function isMobile
	 * @return {boolean} Returns true if the current device is a mobile device, otherwise returns false.
	 */
	isMobile() {
		return window.innerWidth < 576;
	}

	/**
	 * Sets the click listeners and handles events for adding content.
	 *
	 * @return {void}
	 */
	addContent() {
		this.setTabClickListeners();
		this.setListItemClickListeners();
		this.setStationsCloseClickListener();
		this.setTrainsCloseClickListener();
		this.setDetailsCloseClickListener( 'station' );
		this.setDetailsCloseClickListener( 'train' );
		this.setFooterIconClickListener();
		this.setFooterClickListener();
	}
}

/**
 * Initializes a Google Map.
 *
 * @return {void}
 */
async function initializeGoogleMap() {
	const mapOptions = {
		zoom: 13,
		center: { lat: 28.54849, lng: -81.38105 }, // Default center, will be overridden in class
		mapId: window.mapSettings.mapId || 'ca5af54040714437',
		disableDefaultUI: true,
	};

	const customMap = new CustomGoogleMap( 'dvMap', mapOptions );
	await customMap.initialize();
}

document.addEventListener( 'DOMContentLoaded', function () {
	initializeGoogleMap();
} );
